import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { inventoryCountAPI, productsAPI, suppliersAPI } from '../lib/api';
import { formatNumber, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardText, Warehouse, Storefront, Check, Warning, ArrowUp, ArrowDown, Equals, MagnifyingGlass, ChartBar, Plus, Package } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryCount() {
  const [template, setTemplate] = useState(null);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [location, setLocation] = useState('warehouse');
  const [countData, setCountData] = useState({});
  const [notes, setNotes] = useState('');
  const [countDate, setCountDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('count');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Aylık Analiz State
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisMonth, setAnalysisMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Yeni Ürün Ekleme State
  const [newProductModalOpen, setNewProductModalOpen] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: '',
    category: '',
    unit: 'kg',
    unit_price: '',
    min_stock_level: '',
    supplier_id: ''
  });
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Kategoriler
  const categories = [
    { value: 'gida', label: 'Gıda' },
    { value: 'sebze', label: 'Sebze' },
    { value: 'meyve', label: 'Meyve' },
    { value: 'et', label: 'Et' },
    { value: 'sut', label: 'Süt Ürünleri' },
    { value: 'icecek', label: 'İçecek' },
    { value: 'temizlik', label: 'Temizlik' },
    { value: 'diger', label: 'Diğer' }
  ];

  // Birimler
  const units = [
    { value: 'kg', label: 'Kilogram (kg)' },
    { value: 'adet', label: 'Adet' },
    { value: 'lt', label: 'Litre (lt)' },
    { value: 'paket', label: 'Paket' },
    { value: 'kutu', label: 'Kutu' },
    { value: 'koli', label: 'Koli' }
  ];

  useEffect(() => {
    fetchData();
    fetchSuppliers();
  }, [location]);

  const fetchSuppliers = async () => {
    try {
      const res = await suppliersAPI.getAll();
      setSuppliers(res.data || []);
    } catch (error) {
      console.error('Tedarikçiler yüklenemedi:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templateRes, countsRes] = await Promise.all([
        inventoryCountAPI.getTemplate(location),
        inventoryCountAPI.getAll(location),
      ]);
      setTemplate(templateRes.data);
      setCounts(countsRes.data);
      
      // Initialize count data with empty values
      const initialData = {};
      templateRes.data.items.forEach(item => {
        initialData[item.product_id] = '';
      });
      setCountData(initialData);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCountChange = (productId, value) => {
    setCountData({
      ...countData,
      [productId]: value,
    });
  };

  const handleSubmitCount = async () => {
    const items = Object.entries(countData)
      .filter(([_, value]) => value !== '' && value !== null)
      .map(([productId, value]) => ({
        product_id: productId,
        counted_quantity: parseFloat(value),
      }));
    
    if (items.length === 0) {
      toast.error('En az bir ürün için sayım girmelisiniz');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await inventoryCountAPI.submit({
        location,
        items,
        notes: notes || undefined,
      });
      
      toast.success(`Sayım kaydedildi ve stoklar güncellendi! ${res.data.items_with_difference} üründe fark vardı.`);
      setNotes('');
      setCountData({});  // Formu temizle
      fetchData();
      setActiveTab('history');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sayım kaydedilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Yeni Ürün Duplicate Kontrolü
  const checkDuplicate = async (name) => {
    if (!name || name.trim().length < 2) {
      setDuplicateMatches([]);
      setShowDuplicateWarning(false);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/products/check-duplicate`, {
        params: { name: name.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.has_matches) {
        setDuplicateMatches(res.data.matches);
        setShowDuplicateWarning(true);
      } else {
        setDuplicateMatches([]);
        setShowDuplicateWarning(false);
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Yeni Ürün Modal Aç
  const openNewProductModal = () => {
    setNewProductData({
      name: '',
      category: '',
      unit: 'kg',
      unit_price: '',
      min_stock_level: '',
      supplier_id: ''
    });
    setDuplicateMatches([]);
    setShowDuplicateWarning(false);
    setNewProductModalOpen(true);
  };

  // Mevcut Ürünü Sayıma Ekle
  const addExistingProductToCount = (product) => {
    // Template'e bu ürün zaten var mı kontrol et
    const existsInTemplate = template?.items?.some(item => item.product_id === product.id);
    
    if (existsInTemplate) {
      toast.info(`"${product.name}" zaten sayım listesinde mevcut`);
      setNewProductModalOpen(false);
      return;
    }
    
    // Template'e ekle
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      category: product.category,
      unit: product.unit,
      system_quantity: 0,
      counted_quantity: null
    };
    
    setTemplate(prev => ({
      ...prev,
      items: [...(prev?.items || []), newItem]
    }));
    
    // CountData'ya ekle
    setCountData(prev => ({
      ...prev,
      [product.id]: ''
    }));
    
    toast.success(`"${product.name}" sayım listesine eklendi`);
    setNewProductModalOpen(false);
  };

  // Yeni Ürün Oluştur
  const createNewProduct = async (forceCreate = false) => {
    if (!newProductData.name || !newProductData.category || !newProductData.unit) {
      toast.error('Ürün adı, kategori ve birim zorunludur');
      return;
    }
    
    // Duplicate uyarısı varsa ve force değilse dur
    if (showDuplicateWarning && !forceCreate) {
      toast.warning('Benzer ürünler mevcut. Yine de oluşturmak için "Yeni Ürün Oluştur" butonuna tıklayın.');
      return;
    }
    
    setCreatingProduct(true);
    try {
      const productData = {
        name: newProductData.name.trim(),
        category: newProductData.category,
        unit: newProductData.unit,
        unit_price: newProductData.unit_price ? parseFloat(newProductData.unit_price) : 0,
        min_stock_level: newProductData.min_stock_level ? parseFloat(newProductData.min_stock_level) : 0,
        supplier_id: newProductData.supplier_id || null
      };
      
      const res = await productsAPI.create(productData);
      const newProduct = res.data;
      
      // Yeni ürünü template'e ekle
      const newItem = {
        product_id: newProduct.id,
        product_name: newProduct.name,
        category: newProduct.category,
        unit: newProduct.unit,
        system_quantity: 0,
        counted_quantity: null
      };
      
      setTemplate(prev => ({
        ...prev,
        items: [...(prev?.items || []), newItem]
      }));
      
      // CountData'ya ekle
      setCountData(prev => ({
        ...prev,
        [newProduct.id]: ''
      }));
      
      toast.success(`"${newProduct.name}" ürünü oluşturuldu ve sayım listesine eklendi`);
      setNewProductModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ürün oluşturulamadı');
    } finally {
      setCreatingProduct(false);
    }
  };

  const handleApplyCount = async (countId) => {
    if (!window.confirm('Bu sayım sonuçlarını uygulamak istediğinizden emin misiniz? Stok miktarları güncellenecektir.')) {
      return;
    }
    
    try {
      await inventoryCountAPI.apply(countId);
      toast.success('Stok düzeltmeleri uygulandı');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  // Aylık Analiz Fetch
  const fetchMonthlyAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/inventory-counts/monthly-analysis`, {
        params: {
          location,
          start_month: analysisMonth,
          end_month: analysisMonth
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysisData(res.data);
    } catch (error) {
      toast.error('Analiz yüklenemedi');
      setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const getDifferenceIcon = (diff) => {
    if (diff > 0) return <ArrowUp size={14} className="text-secondary" />;
    if (diff < 0) return <ArrowDown size={14} className="text-destructive" />;
    return <Equals size={14} className="text-muted-foreground" />;
  };

  const getDifferenceClass = (diff) => {
    if (diff > 0) return 'text-secondary';
    if (diff < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  // Filtrelenmiş ürünler
  const filteredItems = useMemo(() => {
    if (!template?.items) return [];
    if (!searchQuery.trim()) return template.items;
    
    const query = searchQuery.toLowerCase().trim();
    return template.items.filter(item => 
      item.product_name.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query)
    );
  }, [template?.items, searchQuery]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Stok Sayım
            </h1>
            <p className="mt-2 text-muted-foreground">
              Depo, hazırlık mutfağı ve mağaza stok sayımı yapın
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={location === 'warehouse' ? 'default' : 'outline'}
              onClick={() => setLocation('warehouse')}
              className={location === 'warehouse' ? 'bg-secondary hover:bg-secondary/90' : ''}
              data-testid="location-warehouse"
            >
              <Warehouse size={18} className="mr-2" />
              Depo
            </Button>
            <Button
              variant={location === 'prep' ? 'default' : 'outline'}
              onClick={() => setLocation('prep')}
              className={location === 'prep' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
              data-testid="location-prep"
            >
              <ClipboardText size={18} className="mr-2" />
              Hazırlık Mutfağı
            </Button>
            <Button
              variant={location === 'store' ? 'default' : 'outline'}
              onClick={() => setLocation('store')}
              className={location === 'store' ? 'bg-primary hover:bg-primary/90' : ''}
              data-testid="location-store"
            >
              <Storefront size={18} className="mr-2" />
              Mağaza/Ocak
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="count" data-testid="tab-count">Sayım Yap</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Sayım Geçmişi</TabsTrigger>
            <TabsTrigger value="analysis" data-testid="tab-analysis">
              <ChartBar size={16} className="mr-1" />
              Aylık Analiz
            </TabsTrigger>
          </TabsList>

          <TabsContent value="count" className="mt-6 space-y-4">
            <div className="border border-border bg-card rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <ClipboardText size={24} weight="duotone" className="text-primary" />
                  <div>
                    <h2 className="font-heading text-lg font-semibold">
                      {location === 'warehouse' ? 'Depo' : location === 'prep' ? 'Hazırlık Mutfağı' : 'Mağaza/Ocak'} Sayım Şablonu
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Oluşturulma: {template?.generated_at ? formatDate(template.generated_at) : '-'}
                    </p>
                  </div>
                </div>
                
                {/* Ürün Arama */}
                <div className="relative w-full sm:w-72">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Ürün ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10"
                    data-testid="search-products"
                  />
                </div>
              </div>

              {/* Arama Sonucu Bilgisi */}
              {searchQuery && (
                <p className="text-sm text-muted-foreground mb-2">
                  {filteredItems.length} / {template?.items?.length || 0} ürün gösteriliyor
                </p>
              )}

              {/* Yeni Ürün Ekle Butonu */}
              <div className="flex justify-end mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openNewProductModal}
                  className="gap-2"
                  data-testid="add-new-product-btn"
                >
                  <Plus size={16} />
                  Yeni Ürün Ekle
                </Button>
              </div>

              <div className="overflow-auto max-h-[500px]">
                <Table className="data-table">
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow className="border-b border-border">
                      <TableHead className="py-3">Ürün</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Sistem Miktarı</TableHead>
                      <TableHead className="text-right w-32">Sayılan Miktar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.product_id} className="border-b border-border/40">
                        <TableCell className="py-2 font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-muted-foreground">{item.category}</TableCell>
                        <TableCell className="text-right font-data">
                          {formatNumber(item.system_quantity)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            className="h-9 w-28 text-right font-data"
                            value={countData[item.product_id] || ''}
                            onChange={(e) => handleCountChange(item.product_id, e.target.value)}
                            data-testid={`count-input-${item.product_id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-48">
                <Label>Sayım Tarihi</Label>
                <Input
                  type="date"
                  value={countDate}
                  onChange={(e) => setCountDate(e.target.value)}
                  className="mt-2 h-11"
                  data-testid="count-date"
                />
              </div>
              <div className="flex-1">
                <Label>Sayım Notu</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Sayım ile ilgili notlar..."
                  className="mt-2"
                  rows={3}
                  data-testid="count-notes"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSubmitCount}
                  disabled={submitting}
                  className="h-12 px-8 bg-primary hover:bg-primary/90"
                  data-testid="submit-count"
                >
                  {submitting ? 'Kaydediliyor...' : 'Sayımı Kaydet'}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <div className="border border-border bg-card rounded-xl overflow-hidden">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-border bg-muted/30">
                    <TableHead className="py-4">Tarih</TableHead>
                    <TableHead>Lokasyon</TableHead>
                    <TableHead className="text-center">Toplam Ürün</TableHead>
                    <TableHead className="text-center">Farklı Ürün</TableHead>
                    <TableHead>Not</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {counts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <ClipboardText size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">Henüz sayım kaydı bulunmuyor</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    counts.map((count) => (
                      <TableRow key={count.id} className="table-row-hover border-b border-border/40" data-testid={`count-row-${count.id}`}>
                        <TableCell className="py-3">{formatDate(count.created_at)}</TableCell>
                        <TableCell>
                          <span className={`badge-pill ${count.location === 'prep' ? 'bg-amber-100 text-amber-800' : 'bg-muted'}`}>
                            {count.location === 'warehouse' ? 'Depo' : count.location === 'prep' ? 'Hazırlık' : 'Mağaza'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center font-data">{count.total_items}</TableCell>
                        <TableCell className="text-center">
                          {count.items_with_difference > 0 ? (
                            <span className="badge-pill status-low">{count.items_with_difference}</span>
                          ) : (
                            <span className="badge-pill status-ok">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{count.notes || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedCount(count);
                                setDetailDialogOpen(true);
                              }}
                            >
                              Detay
                            </Button>
                            {count.applied ? (
                              <div className="flex flex-col items-end">
                                <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                  <Check size={14} className="mr-1" />
                                  Uygulandı
                                </span>
                                {count.applied_at && (
                                  <span className="text-[10px] text-muted-foreground mt-0.5">
                                    {formatDate(count.applied_at)}
                                  </span>
                                )}
                              </div>
                            ) : count.items_with_difference > 0 ? (
                              <Button
                                size="sm"
                                className="bg-secondary hover:bg-secondary/90"
                                onClick={() => handleApplyCount(count.id)}
                              >
                                <Check size={16} className="mr-1" />
                                Uygula
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Aylık Analiz Tab */}
          <TabsContent value="analysis" className="mt-6 space-y-4">
            <div className="border border-border bg-card rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <ChartBar size={24} weight="duotone" className="text-primary" />
                  <div>
                    <h2 className="font-heading text-lg font-semibold">
                      {location === 'warehouse' ? 'Depo' : location === 'prep' ? 'Hazırlık Mutfağı' : 'Mağaza/Ocak'} Aylık Stok Analizi
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Ay başı sayım + Girişler - Sevkiyatlar = Teorik Stok
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="month"
                    value={analysisMonth}
                    onChange={(e) => setAnalysisMonth(e.target.value)}
                    className="w-40"
                  />
                  <Button onClick={fetchMonthlyAnalysis} disabled={analysisLoading}>
                    {analysisLoading ? 'Yükleniyor...' : 'Analiz Et'}
                  </Button>
                </div>
              </div>

              {/* Özet Kartlar */}
              {analysisData?.summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Toplam Ürün</p>
                    <p className="text-xl font-bold">{analysisData.summary.total_products}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-red-600">Eksik Çıkan</p>
                    <p className="text-xl font-bold text-red-600">{analysisData.summary.products_with_shortage}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600">Fazla Çıkan</p>
                    <p className="text-xl font-bold text-green-600">{analysisData.summary.products_with_excess}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600">Eşleşen</p>
                    <p className="text-xl font-bold text-blue-600">{analysisData.summary.products_matching}</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-600">En Büyük Fark</p>
                    <p className="text-sm font-medium text-orange-700 truncate">
                      {analysisData.summary.largest_shortage?.difference < 0 
                        ? `${analysisData.summary.largest_shortage.product_name}: ${analysisData.summary.largest_shortage.difference}`
                        : analysisData.summary.largest_excess?.difference > 0
                          ? `${analysisData.summary.largest_excess.product_name}: +${analysisData.summary.largest_excess.difference}`
                          : '-'}
                    </p>
                  </div>
                </div>
              )}

              {/* Analiz Tablosu */}
              {analysisData?.error ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Warning size={48} className="mx-auto mb-2 text-orange-500" />
                  <p>{analysisData.error}</p>
                  <p className="text-sm mt-1">Bulunan sayım: {analysisData.counts_found}</p>
                </div>
              ) : analysisData?.items ? (
                <div className="overflow-auto max-h-[400px]">
                  <Table className="data-table">
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow className="border-b border-border">
                        <TableHead className="py-2">Ürün</TableHead>
                        <TableHead className="text-right">Ay Başı</TableHead>
                        <TableHead className="text-right">Giriş</TableHead>
                        <TableHead className="text-right">Sevkiyat</TableHead>
                        <TableHead className="text-right">Teorik</TableHead>
                        <TableHead className="text-right">Sayım</TableHead>
                        <TableHead className="text-right">Fark</TableHead>
                        <TableHead className="text-right">Fark %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysisData.items.map((item) => (
                        <TableRow 
                          key={item.product_id} 
                          className={item.is_significant ? 'bg-orange-50' : ''}
                        >
                          <TableCell className="font-medium">
                            {item.is_significant && <Warning size={14} className="inline mr-1 text-orange-500" />}
                            {item.product_name}
                          </TableCell>
                          <TableCell className="text-right">{formatNumber(item.start_quantity)} {item.unit}</TableCell>
                          <TableCell className="text-right text-green-600">+{formatNumber(item.entries)}</TableCell>
                          <TableCell className="text-right text-blue-600">-{formatNumber(item.transfers)}</TableCell>
                          <TableCell className="text-right font-medium">{formatNumber(item.theoretical)}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.end_quantity)}</TableCell>
                          <TableCell className={`text-right font-bold ${
                            item.difference < 0 ? 'text-red-600' : item.difference > 0 ? 'text-green-600' : ''
                          }`}>
                            {item.difference > 0 ? '+' : ''}{formatNumber(item.difference)}
                          </TableCell>
                          <TableCell className={`text-right ${
                            item.difference < 0 ? 'text-red-600' : item.difference > 0 ? 'text-green-600' : ''
                          }`}>
                            {item.difference_percent > 0 ? '+' : ''}{item.difference_percent.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <ChartBar size={48} className="mx-auto mb-2" />
                  <p>Ay seçip "Analiz Et" butonuna tıklayın</p>
                </div>
              )}

              {analysisData?.period && (
                <p className="text-xs text-muted-foreground mt-2">
                  Dönem: {analysisData.period.start_date} - {analysisData.period.end_date}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Sayım Detayı - {selectedCount?.location === 'warehouse' ? 'Depo' : selectedCount?.location === 'prep' ? 'Hazırlık Mutfağı' : 'Mağaza/Ocak'}
              </DialogTitle>
            </DialogHeader>
            {selectedCount && (
              <div className="space-y-4">
                <div className="flex gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Tarih:</span>{' '}
                    <span className="font-medium">{formatDate(selectedCount.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Farklı Ürün:</span>{' '}
                    <span className="font-medium">{selectedCount.items_with_difference}</span>
                  </div>
                </div>
                
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="border-b border-border">
                      <TableHead>Ürün</TableHead>
                      <TableHead className="text-right">Sistem</TableHead>
                      <TableHead className="text-right">Sayılan</TableHead>
                      <TableHead className="text-right">Fark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCount.items.map((item) => (
                      <TableRow key={item.product_id} className={item.difference !== 0 ? 'bg-accent/10' : ''}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-right font-data">
                          {formatNumber(item.system_quantity)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right font-data">
                          {formatNumber(item.counted_quantity)} {item.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className={`flex items-center justify-end gap-1 font-data ${getDifferenceClass(item.difference)}`}>
                            {getDifferenceIcon(item.difference)}
                            {item.difference > 0 ? '+' : ''}{formatNumber(item.difference)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Yeni Ürün Ekleme Modalı */}
        <Dialog open={newProductModalOpen} onOpenChange={setNewProductModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package size={20} />
                Sayıma Yeni Ürün Ekle
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Ürün Adı */}
              <div className="space-y-2">
                <Label htmlFor="product-name">Ürün Adı *</Label>
                <Input
                  id="product-name"
                  placeholder="Ör: Domates, Tavuk But, Coca Cola..."
                  value={newProductData.name}
                  onChange={(e) => {
                    setNewProductData({ ...newProductData, name: e.target.value });
                    // Debounce duplicate check
                    clearTimeout(window.duplicateCheckTimeout);
                    window.duplicateCheckTimeout = setTimeout(() => {
                      checkDuplicate(e.target.value);
                    }, 500);
                  }}
                  data-testid="new-product-name"
                />
                {checkingDuplicate && (
                  <p className="text-xs text-muted-foreground">Benzer ürünler kontrol ediliyor...</p>
                )}
              </div>

              {/* Duplicate Uyarısı */}
              {showDuplicateWarning && duplicateMatches.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 font-medium">
                    <Warning size={18} />
                    Benzer ürünler mevcut!
                  </div>
                  <p className="text-sm text-amber-600">
                    Aşağıdaki ürünlerden birini seçebilir veya yeni ürün oluşturabilirsiniz:
                  </p>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {duplicateMatches.map((match) => (
                      <div
                        key={match.id}
                        className="flex items-center justify-between bg-white rounded p-2 border border-amber-100 hover:border-amber-300 cursor-pointer transition-colors"
                        onClick={() => addExistingProductToCount(match)}
                        data-testid={`duplicate-match-${match.id}`}
                      >
                        <div>
                          <p className="font-medium text-sm">{match.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {match.category} / {match.unit} • <span className="font-mono text-[10px]">ID: {match.id.slice(0,8)}...</span>
                            {match.match_type === 'exact' && <span className="ml-2 text-amber-600">(Tam eşleşme)</span>}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:text-amber-900">
                          Seç
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kategori */}
              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select
                  value={newProductData.category}
                  onValueChange={(value) => setNewProductData({ ...newProductData, category: value })}
                >
                  <SelectTrigger data-testid="new-product-category">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Birim */}
              <div className="space-y-2">
                <Label>Birim *</Label>
                <Select
                  value={newProductData.unit}
                  onValueChange={(value) => setNewProductData({ ...newProductData, unit: value })}
                >
                  <SelectTrigger data-testid="new-product-unit">
                    <SelectValue placeholder="Birim seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Birim Fiyatı */}
              <div className="space-y-2">
                <Label htmlFor="product-price">Birim/Alış Fiyatı (₺) <span className="text-muted-foreground">(opsiyonel)</span></Label>
                <Input
                  id="product-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newProductData.unit_price}
                  onChange={(e) => setNewProductData({ ...newProductData, unit_price: e.target.value })}
                  data-testid="new-product-price"
                />
              </div>

              {/* Minimum Stok */}
              <div className="space-y-2">
                <Label htmlFor="product-min-stock">Minimum Stok <span className="text-muted-foreground">(opsiyonel)</span></Label>
                <Input
                  id="product-min-stock"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newProductData.min_stock_level}
                  onChange={(e) => setNewProductData({ ...newProductData, min_stock_level: e.target.value })}
                  data-testid="new-product-min-stock"
                />
              </div>

              {/* Tedarikçi */}
              {suppliers.length > 0 && (
                <div className="space-y-2">
                  <Label>Tedarikçi <span className="text-muted-foreground">(opsiyonel)</span></Label>
                  <Select
                    value={newProductData.supplier_id}
                    onValueChange={(value) => setNewProductData({ ...newProductData, supplier_id: value })}
                  >
                    <SelectTrigger data-testid="new-product-supplier">
                      <SelectValue placeholder="Tedarikçi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Tedarikçi yok</SelectItem>
                      {suppliers.map((sup) => (
                        <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setNewProductModalOpen(false)}>
                İptal
              </Button>
              {showDuplicateWarning ? (
                <Button
                  onClick={() => createNewProduct(true)}
                  disabled={creatingProduct || !newProductData.name || !newProductData.category}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="force-create-product-btn"
                >
                  {creatingProduct ? 'Oluşturuluyor...' : 'Yine de Yeni Ürün Oluştur'}
                </Button>
              ) : (
                <Button
                  onClick={() => createNewProduct(false)}
                  disabled={creatingProduct || !newProductData.name || !newProductData.category}
                  data-testid="create-product-btn"
                >
                  {creatingProduct ? 'Oluşturuluyor...' : 'Ürün Oluştur ve Ekle'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
